/**
 * MH-34 Quản trị người dùng (đợt 3 phần C4; mở rộng theo hợp đồng A0 tài khoản xã
 * mục 4 và 6).
 *
 * Danh sách tài khoản lọc/phân trang PHÍA MÁY CHỦ (mặc định 20 dòng), tạo và sửa
 * đầy đủ (cấp Tỉnh/Xã, đúng 1 xã khi cấp Xã, nhóm quyền đa chọn — cấp Xã chỉ hiện
 * nhóm `dungChoCapXa`), khoá/mở khoá, đặt lại mật khẩu. Mật khẩu ban đầu chỉ hiện
 * MỘT LẦN trong hộp riêng, kèm nút sao chép. Ma trận quyền theo nhóm ở MH-35.
 *
 * Giao diện chỉ lọc để thuận tay; máy chủ vẫn là nơi chặn (FORBIDDEN, INVALID_VALUE,
 * OPERATION_NOT_ALLOWED…) và lỗi được diễn giải thành câu dễ hiểu.
 */
import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viQuanTri } from '../ngon-ngu/vi-quan-tri';
import {
  quanTriApi,
  LoiApi,
  type CapTaiKhoan,
  type DongNguoiDung,
  type DongNhom,
  type TuyChonDanhSachNguoiDung,
} from '../api/quan-tri-api';
import { thietBiApi, type NutDiaBan } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { ChonDiaBanCay } from '../thanh-phan/ChonDiaBanCay';
import { HopXacNhan } from '../thanh-phan/HopXacNhan';
import { TheTag, TruongLoc } from '../thanh-phan/MauGiaoDien';
import './quan-tri.css';

const t = viQuanTri.nguoiDung;

/** Lớp API dùng ở màn hình — ghi đè được trong kiểm thử. */
export type ApiMH34 = Pick<
  typeof quanTriApi,
  'danhSachNguoiDung' | 'taoNguoiDung' | 'suaNguoiDung' | 'khoa' | 'moKhoa' | 'datLaiMatKhau' | 'danhSachNhom'
> & { cayXa: (signal?: AbortSignal) => Promise<NutDiaBan[]> };

const API_MAC_DINH: ApiMH34 = {
  danhSachNguoiDung: quanTriApi.danhSachNguoiDung,
  taoNguoiDung: quanTriApi.taoNguoiDung,
  suaNguoiDung: quanTriApi.suaNguoiDung,
  khoa: quanTriApi.khoa,
  moKhoa: quanTriApi.moKhoa,
  datLaiMatKhau: quanTriApi.datLaiMatKhau,
  danhSachNhom: quanTriApi.danhSachNhom,
  cayXa: (signal) => thietBiApi.cayDiaBan({ cap: 'xa' }, signal),
};

/** Bỏ dấu chấm/khoảng trắng cuối để so hai câu thông báo cùng ý. */
const chuanCau = (s: string): string =>
  s
    .trim()
    .replace(/[.\s]+$/u, '')
    .toLowerCase();

/**
 * Diễn giải lỗi API thành câu dễ hiểu, kèm chi tiết máy chủ trả về. Chi tiết trùng
 * câu diễn giải (vd. DUPLICATE "Tên đăng nhập đã được sử dụng") thì không lặp lại.
 */
export function moTaLoi(err: unknown): string {
  if (!(err instanceof LoiApi)) return t.loi.macDinh;
  const coSan = (t.loi as Record<string, string>)[err.ma];
  const goc = coSan ?? t.loi.macDinh;
  return err.message && chuanCau(err.message) !== chuanCau(goc)
    ? `${goc} ${dienVao(t.chiTietLoi, { chiTiet: err.message })}`
    : goc;
}

/** Tên địa bàn hiển thị ở cột "Địa bàn": theo `tenPhamVi`; tỉnh rỗng → "Toàn tỉnh". */
export function tenDiaBanCua(d: Pick<DongNguoiDung, 'tenPhamVi' | 'phamViDiaBan'>): string {
  const ds = d.tenPhamVi?.length ? d.tenPhamVi : d.phamViDiaBan;
  return ds.join(', ') || t.toanTinh;
}

/** Nhóm hiển thị được trong biểu mẫu theo cấp: cấp Xã chỉ các nhóm `dungChoCapXa`. */
export function nhomTheoCap(ds: DongNhom[], cap: CapTaiKhoan): DongNhom[] {
  return cap === 'xa' ? ds.filter((n) => n.dungChoCapXa === true) : ds;
}

/* ------------------------- Hộp mật khẩu hiện một lần ------------------------ */

/**
 * Hộp hiển thị mật khẩu ban đầu MỘT LẦN (dùng chung với MH-39 khi tạo tỉnh mới):
 * `phuLuc` cho phép màn gọi chèn thêm nội dung (vd. hướng dẫn bước tiếp).
 */
export function HopMatKhauMotLan({
  tieuDe,
  matKhau,
  onDong,
  phuLuc,
}: {
  tieuDe: string;
  matKhau: string;
  onDong: () => void;
  phuLuc?: ReactNode;
}): ReactNode {
  const [daChep, datDaChep] = useState(false);
  const idTieuDe = useId();
  const o = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    o.current?.focus();
    o.current?.select();
  }, []);
  const chep = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(matKhau);
      datDaChep(true);
    } catch {
      // Trình duyệt chặn clipboard: người dùng tự bôi đen ô để sao chép.
      o.current?.select();
    }
  };
  return (
    <div className="man-che" role="presentation">
      <div
        className="hop-thoai"
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTieuDe}
        data-vung="mat-khau-mot-lan"
      >
        <h2 id={idTieuDe}>{tieuDe}</h2>
        <p role="alert">{t.matKhau.canhBao}</p>
        <label htmlFor="nd-mat-khau-mot-lan">{t.matKhau.nhan}</label>
        <div className="man-hinh__hanh-dong">
          <input
            id="nd-mat-khau-mot-lan"
            ref={o}
            readOnly
            value={matKhau}
            spellCheck={false}
            autoComplete="off"
            onFocus={(e) => e.target.select()}
          />
          <button type="button" className="nut nut--nho" onClick={() => void chep()}>
            {daChep ? t.matKhau.daSaoChep : t.matKhau.saoChep}
          </button>
        </div>
        <p className="truong__mo-ta" data-vung="ghi-chu-doi-mat-khau">
          {t.matKhau.ghiChu}
        </p>
        {phuLuc}
        <div className="hop-thoai__nut">
          <button type="button" className="nut nut--chinh" onClick={onDong}>
            {t.matKhau.dong}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Biểu mẫu tạo/sửa ---------------------------- */

interface BieuMauProps {
  /** Vắng = tạo mới. */
  banDau: DongNguoiDung | null;
  dsNhom: DongNhom[];
  dsXa: NutDiaBan[];
  api: ApiMH34;
  onXong: (kq: { matKhauBanDau?: string; tenDangNhap: string }) => void;
  onHuy: () => void;
}

export function BieuMauNguoiDung({ banDau, dsNhom, dsXa, api, onXong, onHuy }: BieuMauProps): ReactNode {
  const laSua = banDau !== null;
  const [tenDangNhap, datTenDangNhap] = useState(banDau?.tenDangNhap ?? '');
  const [hoTen, datHoTen] = useState(banDau?.hoTen ?? '');
  const [email, datEmail] = useState(banDau?.email ?? '');
  const [dienThoai, datDienThoai] = useState(banDau?.dienThoai ?? '');
  const [diaChi, datDiaChi] = useState(banDau?.diaChi ?? '');
  const [cap, datCap] = useState<CapTaiKhoan>(banDau?.cap ?? 'tinh');
  const [maXa, datMaXa] = useState(banDau?.cap === 'xa' ? (banDau.phamViDiaBan[0] ?? '') : '');
  const [nhomId, datNhomId] = useState<string[]>(banDau?.nhom.map((n) => n.id) ?? []);
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);
  const idTieuDe = useId();

  const nhomHien = useMemo(() => nhomTheoCap(dsNhom, cap), [dsNhom, cap]);

  const doiCap = (moi: CapTaiKhoan): void => {
    datCap(moi);
    if (moi === 'xa') {
      // Bỏ các nhóm không dùng được cho cấp xã khỏi lựa chọn hiện tại.
      const hopLe = new Set(nhomTheoCap(dsNhom, 'xa').map((n) => n.id));
      datNhomId((ds) => ds.filter((id) => hopLe.has(id)));
    } else {
      datMaXa('');
    }
  };

  const batNhom = (id: string): void =>
    datNhomId((ds) => (ds.includes(id) ? ds.filter((x) => x !== id) : [...ds, id]));

  const gui = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    datLoi(null);
    if ((!laSua && !tenDangNhap.trim()) || !hoTen.trim()) {
      datLoi(t.form.thieuTruong);
      return;
    }
    if (cap === 'xa' && !maXa) {
      datLoi(t.form.thieuXa);
      return;
    }
    // Cấp tỉnh: giữ phạm vi tỉnh cũ ([] hoặc [maTinh]) nếu trước đó đã là tỉnh.
    const phamViDiaBan = cap === 'xa' ? [maXa] : banDau && banDau.cap !== 'xa' ? banDau.phamViDiaBan : [];
    // Ô trống: không gửi; riêng khi SỬA mà trước đó có giá trị thì gửi chuỗi rỗng để
    // XOÁ (hợp đồng A0 hồ sơ – thông báo mục 4).
    const lienHe = (v: string, cu: string | null | undefined): string | undefined =>
      v.trim() || (laSua && cu ? '' : undefined);
    const chung = {
      hoTen: hoTen.trim(),
      email: lienHe(email, banDau?.email),
      dienThoai: lienHe(dienThoai, banDau?.dienThoai),
      diaChi: lienHe(diaChi, banDau?.diaChi),
      cap,
      nhomId,
      phamViDiaBan,
    };
    datDangGui(true);
    try {
      if (banDau) {
        await api.suaNguoiDung(banDau.id, chung);
        onXong({ tenDangNhap: banDau.tenDangNhap });
      } else {
        const kq = await api.taoNguoiDung({ tenDangNhap: tenDangNhap.trim(), ...chung });
        onXong({ tenDangNhap: tenDangNhap.trim(), matKhauBanDau: kq.matKhauBanDau });
      }
    } catch (err) {
      datLoi(moTaLoi(err));
    } finally {
      datDangGui(false);
    }
  };

  return (
    <div className="man-che" role="presentation">
      <form
        className="hop-thoai bieu-mau-nguoi-dung"
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTieuDe}
        data-vung="bieu-mau-nguoi-dung"
        onSubmit={(e) => void gui(e)}
        noValidate
      >
        <h2 id={idTieuDe}>
          {laSua ? dienVao(t.form.tieuDeSua, { ten: banDau.tenDangNhap }) : t.form.tieuDeTao}
        </h2>
        {!laSua ? (
          <div className="truong">
            <label htmlFor="nd-ten-dang-nhap">
              {t.form.tenDangNhap}
              <span className="truong__bat-buoc">*</span>
            </label>
            <input
              id="nd-ten-dang-nhap"
              value={tenDangNhap}
              onChange={(e) => datTenDangNhap(e.target.value)}
              autoComplete="off"
            />
          </div>
        ) : null}
        <div className="truong">
          <label htmlFor="nd-ho-ten">
            {t.form.hoTen}
            <span className="truong__bat-buoc">*</span>
          </label>
          <input id="nd-ho-ten" value={hoTen} onChange={(e) => datHoTen(e.target.value)} />
        </div>
        <div className="truong">
          <label htmlFor="nd-email">{t.form.email}</label>
          <input id="nd-email" type="email" value={email} onChange={(e) => datEmail(e.target.value)} />
        </div>
        <div className="truong">
          <label htmlFor="nd-dien-thoai">{t.form.dienThoai}</label>
          <input
            id="nd-dien-thoai"
            type="tel"
            value={dienThoai}
            onChange={(e) => datDienThoai(e.target.value)}
          />
        </div>
        <div className="truong">
          <label htmlFor="nd-dia-chi">{t.form.diaChi}</label>
          <input
            id="nd-dia-chi"
            value={diaChi}
            maxLength={300}
            autoComplete="off"
            onChange={(e) => datDiaChi(e.target.value)}
          />
        </div>

        <fieldset className="truong">
          <legend>{t.form.cap}</legend>
          <label>
            <input type="radio" name="nd-cap" checked={cap === 'tinh'} onChange={() => doiCap('tinh')} />{' '}
            {t.form.capTinh}
          </label>
          <label>
            <input type="radio" name="nd-cap" checked={cap === 'xa'} onChange={() => doiCap('xa')} />{' '}
            {t.form.capXa}
          </label>
        </fieldset>

        {cap === 'xa' ? (
          <div className="truong">
            <label htmlFor="nd-xa">
              {t.form.xa}
              <span className="truong__bat-buoc">*</span>
            </label>
            <ChonDiaBanCay
              id="nd-xa"
              nhan={t.form.xa}
              giaTri={maXa}
              cay={dsXa}
              onDoi={datMaXa}
              nhanTatCa={t.form.chonXa}
              goiYTim={t.form.timXa}
              boQuaKhoaXa
            />
          </div>
        ) : null}

        <fieldset className="truong" data-vung="nd-nhom">
          <legend>{t.form.nhom}</legend>
          {cap === 'xa' ? <p className="truong__mo-ta">{t.form.goiYNhomXa}</p> : null}
          {nhomHien.length === 0 ? <p>{t.form.khongCoNhom}</p> : null}
          {nhomHien.map((n) => (
            <label key={n.id} className="bieu-mau-nguoi-dung__nhom">
              <input type="checkbox" checked={nhomId.includes(n.id)} onChange={() => batNhom(n.id)} /> {n.ten}
            </label>
          ))}
        </fieldset>

        {loi ? (
          <p className="dai-thong-bao dai-thong-bao--loi" role="alert">
            {loi}
          </p>
        ) : null}
        <div className="hop-thoai__nut">
          <button type="button" className="nut" onClick={onHuy}>
            {t.form.huy}
          </button>
          <button type="submit" className="nut nut--chinh" disabled={dangGui}>
            {dangGui ? t.form.dangLuu : laSua ? t.form.luu : t.form.tao}
          </button>
        </div>
      </form>
    </div>
  );
}

/* --------------------------------- Màn hình -------------------------------- */

export interface MH34Props {
  api?: ApiMH34;
}

export function MH34NguoiDung({ api = API_MAC_DINH }: MH34Props = {}): ReactNode {
  const [moc, datMoc] = useState(0);
  /** null = đóng; 'tao' = tạo mới; dòng = sửa dòng đó. */
  const [bieuMau, datBieuMau] = useState<null | 'tao' | DongNguoiDung>(null);
  const [matKhauHien, datMatKhauHien] = useState<{ tieuDe: string; matKhau: string } | null>(null);
  const [canDatLai, datCanDatLai] = useState<DongNguoiDung | null>(null);
  const [thongBao, datThongBao] = useState<string | null>(null);
  const [loiThaoTac, datLoiThaoTac] = useState<string | null>(null);

  // Bộ lọc — gửi lên máy chủ.
  const [locCap, datLocCap] = useState<'' | CapTaiKhoan>('');
  const [locXa, datLocXa] = useState('');
  const [locNhom, datLocNhom] = useState('');
  const [locTrangThai, datLocTrangThai] = useState('');
  const [oTuKhoa, datOTuKhoa] = useState('');
  const [tuKhoa, datTuKhoa] = useState('');
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState(SO_DONG_MAC_DINH);

  const dangLoc = locCap !== '' || locXa !== '' || locNhom !== '' || locTrangThai !== '' || tuKhoa !== '';

  const tc: TuyChonDanhSachNguoiDung = {
    page: trang,
    pageSize: soDong,
    cap: locCap || undefined,
    maDiaBan: locXa || undefined,
    nhomId: locNhom || undefined,
    trangThai: locTrangThai || undefined,
    tuKhoa: tuKhoa || undefined,
  };

  const kq = dungTaiDuLieu<{ items: DongNguoiDung[]; total: number }>({
    tai: (signal) => api.danhSachNguoiDung(tc, signal),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [moc, trang, soDong, locCap, locXa, locNhom, locTrangThai, tuKhoa],
  });

  const nhom = dungTaiDuLieu<{ nhom: DongNhom[] }>({
    tai: (signal) => api.danhSachNhom(signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [],
  });
  const dsNhom = nhom.duLieu?.nhom ?? [];

  const xa = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => api.cayXa(signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });
  const dsXa = useMemo(() => (xa.duLieu ?? []).filter((n) => n.cap === 'xa'), [xa.duLieu]);

  const taiLai = (): void => datMoc((x) => x + 1);

  /** Đổi một bộ lọc thì về trang 1. */
  const doiLoc =
    <V,>(dat: (v: V) => void) =>
    (v: V): void => {
      dat(v);
      datTrang(1);
    };

  const xoaLoc = (): void => {
    datLocCap('');
    datLocXa('');
    datLocNhom('');
    datLocTrangThai('');
    datOTuKhoa('');
    datTuKhoa('');
    datTrang(1);
  };

  const hanhDong = async (d: DongNguoiDung, kieu: 'khoa' | 'mo-khoa'): Promise<void> => {
    datThongBao(null);
    datLoiThaoTac(null);
    try {
      if (kieu === 'khoa') await api.khoa(d.id);
      else await api.moKhoa(d.id);
      datThongBao(kieu === 'khoa' ? vi.nguoiDung.daKhoa : vi.nguoiDung.daMoKhoa);
      taiLai();
    } catch (err) {
      datLoiThaoTac(moTaLoi(err));
    }
  };

  const datLaiMatKhau = async (d: DongNguoiDung): Promise<void> => {
    datCanDatLai(null);
    datThongBao(null);
    datLoiThaoTac(null);
    try {
      const kq2 = await api.datLaiMatKhau(d.id);
      datMatKhauHien({
        tieuDe: dienVao(t.matKhau.tieuDeDatLai, { ten: d.tenDangNhap }),
        matKhau: kq2.matKhauMoi,
      });
      taiLai();
    } catch (err) {
      datLoiThaoTac(moTaLoi(err));
    }
  };

  const cot: CotBang<DongNguoiDung>[] = [
    { khoa: 'tenDangNhap', nhan: vi.nguoiDung.cot.tenDangNhap, ve: (d) => d.tenDangNhap },
    { khoa: 'hoTen', nhan: vi.nguoiDung.cot.hoTen, ve: (d) => d.hoTen },
    { khoa: 'email', nhan: vi.nguoiDung.cot.email, ve: (d) => d.email ?? '—' },
    {
      khoa: 'cap',
      nhan: t.cot.cap,
      ve: (d) => <span data-cap={d.cap ?? 'tinh'}>{t.cap[d.cap === 'xa' ? 'xa' : 'tinh']}</span>,
    },
    { khoa: 'diaBan', nhan: t.cot.diaBan, ve: (d) => tenDiaBanCua(d) },
    { khoa: 'nhom', nhan: vi.nguoiDung.cot.nhom, ve: (d) => d.nhom.map((n) => n.ten).join(', ') || '—' },
    {
      khoa: 'trangThai',
      nhan: vi.nguoiDung.cot.trangThai,
      ve: (d) => (
        <span className="man-hinh__hanh-dong">
          <TheTag kieu={d.trangThai === 'locked' ? 'do' : 'ok'}>
            {vi.nguoiDung.trangThai[d.trangThai as 'active' | 'locked'] ?? d.trangThai}
          </TheTag>
          {d.phaiDoiMatKhau ? (
            <span data-vung="cho-doi-mat-khau">
              <TheTag kieu="vang">{t.choDoiMatKhau}</TheTag>
            </span>
          ) : null}
        </span>
      ),
    },
    {
      khoa: 'hanhDong',
      nhan: '',
      ve: (d) => (
        <div className="man-hinh__hanh-dong">
          <button type="button" className="nut nut--nho" onClick={() => datBieuMau(d)}>
            {t.sua}
          </button>
          {d.trangThai === 'locked' ? (
            <button type="button" className="nut nut--nho" onClick={() => void hanhDong(d, 'mo-khoa')}>
              {vi.nguoiDung.moKhoa}
            </button>
          ) : (
            <button type="button" className="nut nut--nho" onClick={() => void hanhDong(d, 'khoa')}>
              {vi.nguoiDung.khoa}
            </button>
          )}
          <button type="button" className="nut nut--nho" onClick={() => datCanDatLai(d)}>
            {vi.nguoiDung.datLaiMatKhau}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="man-hinh man-hinh--nguoi-dung">
      <header className="man-hinh__dau">
        <h1>{vi.nguoiDung.tieuDe}</h1>
        <div className="man-hinh__hanh-dong">
          <Link to="/quan-tri/nhom-quyen" className="nut">
            {vi.nhomQuyen.tieuDe}
          </Link>
          <button type="button" className="nut nut--chinh" onClick={() => datBieuMau('tao')}>
            {vi.nguoiDung.taoMoi}
          </button>
        </div>
      </header>

      <div className="thanh-loc-mau" role="search" data-vung="thanh-loc">
        <TruongLoc nhan={t.loc.cap} htmlFor="nd-loc-cap">
          <select
            id="nd-loc-cap"
            value={locCap}
            onChange={(e) => doiLoc(datLocCap)(e.target.value as '' | CapTaiKhoan)}
          >
            <option value="">{t.loc.tatCaCap}</option>
            <option value="tinh">{t.cap.tinh}</option>
            <option value="xa">{t.cap.xa}</option>
          </select>
        </TruongLoc>
        <TruongLoc nhan={t.loc.xa} htmlFor="nd-loc-xa">
          <ChonDiaBanCay
            id="nd-loc-xa"
            nhan={t.loc.xa}
            giaTri={locXa}
            cay={dsXa}
            onDoi={doiLoc(datLocXa)}
            nhanTatCa={t.loc.tatCaXa}
            goiYTim={t.loc.timXa}
            boQuaKhoaXa
          />
        </TruongLoc>
        <TruongLoc nhan={t.loc.nhom} htmlFor="nd-loc-nhom">
          <select id="nd-loc-nhom" value={locNhom} onChange={(e) => doiLoc(datLocNhom)(e.target.value)}>
            <option value="">{t.loc.tatCaNhom}</option>
            {dsNhom.map((n) => (
              <option key={n.id} value={n.id}>
                {n.ten}
              </option>
            ))}
          </select>
        </TruongLoc>
        <TruongLoc nhan={t.loc.trangThai} htmlFor="nd-loc-trang-thai">
          <select
            id="nd-loc-trang-thai"
            value={locTrangThai}
            onChange={(e) => doiLoc(datLocTrangThai)(e.target.value)}
          >
            <option value="">{t.loc.tatCaTrangThai}</option>
            <option value="active">{vi.nguoiDung.trangThai.active}</option>
            <option value="locked">{vi.nguoiDung.trangThai.locked}</option>
          </select>
        </TruongLoc>
        <TruongLoc nhan={t.loc.tuKhoa} htmlFor="nd-loc-tu-khoa">
          <form
            className="man-hinh__hanh-dong"
            onSubmit={(e) => {
              e.preventDefault();
              doiLoc(datTuKhoa)(oTuKhoa.trim());
            }}
          >
            <input
              id="nd-loc-tu-khoa"
              type="search"
              placeholder={t.loc.goiYTuKhoa}
              value={oTuKhoa}
              onChange={(e) => datOTuKhoa(e.target.value)}
            />
            <button type="submit" className="nut">
              {t.loc.tim}
            </button>
          </form>
        </TruongLoc>
      </div>

      {thongBao ? <p role="status">{thongBao}</p> : null}
      {loiThaoTac ? (
        <p className="dai-thong-bao dai-thong-bao--loi" role="alert">
          {loiThaoTac}
        </p>
      ) : null}

      <BangDuLieu<DongNguoiDung>
        nhan={vi.nguoiDung.tieuDe}
        cot={cot}
        dong={kq.duLieu?.items ?? []}
        khoaDong={(d) => d.id}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        dangLoc={dangLoc}
        onXoaLoc={xoaLoc}
        moTaRong={vi.nguoiDung.rongMoTa}
        nhanHanhDongRong={vi.nguoiDung.taoMoi}
        onHanhDongRong={() => datBieuMau('tao')}
      />

      {bieuMau ? (
        <BieuMauNguoiDung
          key={bieuMau === 'tao' ? 'tao' : bieuMau.id}
          banDau={bieuMau === 'tao' ? null : bieuMau}
          dsNhom={dsNhom}
          dsXa={dsXa}
          api={api}
          onHuy={() => datBieuMau(null)}
          onXong={(kqTao) => {
            datBieuMau(null);
            datLoiThaoTac(null);
            if (kqTao.matKhauBanDau) {
              datMatKhauHien({
                tieuDe: dienVao(t.matKhau.tieuDeTao, { ten: kqTao.tenDangNhap }),
                matKhau: kqTao.matKhauBanDau,
              });
            } else {
              datThongBao(dienVao(t.form.daSua, { ten: kqTao.tenDangNhap }));
            }
            taiLai();
          }}
        />
      ) : null}

      {canDatLai ? (
        <HopXacNhan
          tieuDe={vi.nguoiDung.datLaiMatKhau}
          heQua={dienVao(t.xacNhanDatLai, { ten: canDatLai.tenDangNhap })}
          nhanThucHien={vi.nguoiDung.datLaiMatKhau}
          onHuy={() => datCanDatLai(null)}
          onThucHien={() => void datLaiMatKhau(canDatLai)}
        />
      ) : null}

      {matKhauHien ? (
        <HopMatKhauMotLan
          tieuDe={matKhauHien.tieuDe}
          matKhau={matKhauHien.matKhau}
          onDong={() => datMatKhauHien(null)}
        />
      ) : null}
    </div>
  );
}
